import { useParams } from "@solidjs/router";
import { useLocale, hardcoded } from "~/context/locale";
import Seo from "~/components/seo";
import ProjectPage, { findProject } from "~/pages/portfolio/ProjectPage";
import DocPage from "~/pages/portfolio/documentation/DocPage";
import AwesomeListPage from "~/pages/portfolio/awesome/AwesomeListPage";

const AWESOME_SLUG = "awesome-riscv-core";
const AWESOME_NAME = hardcoded("Awesome RISC-V");

const BASE = "https://lyszt.net";
const SITE = "João Luis Almeida Santos";
const PERSON = `${BASE}/#person`;
const IN_LANG = { en: "en", pt: "pt-BR", fr: "fr-FR" };

export default function ProjectRoute() {
  const params = useParams();
  const { t, locale, prefix } = useLocale();
  const project = () => findProject(params.slug);
  const hasDocumentation = () => project()?.hasReadme === true;
  const url = () => `${BASE}${prefix}/portfolio/${params.slug}`;
  const isAwesome = () => params.slug === AWESOME_SLUG;
  const awesomeText = (key) => t(key).replace("{name}", AWESOME_NAME);
  const title = () =>
    isAwesome() ? awesomeText("awesome.seoTitle") : project() ? `${project().seo_title || project().name} — ${SITE}` : `Not Found — ${SITE}`;
  const desc = () => (isAwesome() ? awesomeText("awesome.seoDesc") : project()?.desc || project()?.name || "");
  const ld = () =>
    project()
      ? {
          "@context": "https://schema.org",
          "@graph": [
            isAwesome() ? {
              "@type": "CollectionPage",
              "@id": `${url()}#collection`,
              url: url(),
              name: awesomeText("awesome.seoTitle"),
              description: awesomeText("awesome.seoDesc"),
              about: { "@type": "Thing", name: "RISC-V", sameAs: "https://en.wikipedia.org/wiki/RISC-V" },
              isBasedOn: project().url,
              license: "https://creativecommons.org/licenses/by-sa/4.0/",
              author: { "@type": "Person", "@id": PERSON, name: SITE },
              inLanguage: IN_LANG[locale] ?? "en",
            } : {
              "@type": "SoftwareApplication",
              "@id": `${url()}#software`,
              url: url(),
              name: project().name,
              description: project().desc || project().name,
              codeRepository: project().url,
              ...(project().homepage ? { url: project().homepage } : {}),
              ...(project().lang ? { programmingLanguage: project().lang } : {}),
              author: { "@type": "Person", "@id": PERSON, name: SITE },
              publisher: { "@type": "Person", "@id": PERSON, name: SITE },
              license: "https://opensource.org/licenses/MIT",
            },
            {
              "@type": "WebPage",
              "@id": `${url()}#webpage`,
              url: url(),
              name: `${project().name} — ${SITE}`,
              description: project().desc || project().name,
              isPartOf: { "@id": `${BASE}/#website` },
              author: { "@id": PERSON },
              inLanguage: IN_LANG[locale] ?? "en",
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: `${BASE}${prefix}` },
                { "@type": "ListItem", position: 2, name: t("breadcrumb.portfolio"), item: `${BASE}${prefix}/portfolio` },
                { "@type": "ListItem", position: 3, name: project().name, item: url() },
              ],
            },
          ],
        }
      : null;

  return (
    <main>
      <Seo title={title()} description={desc()} path={`/portfolio/${params.slug}`} ld={ld()} />
      {params.slug === AWESOME_SLUG
        ? <AwesomeListPage slug={params.slug} />
        : hasDocumentation() ? <DocPage /> : <ProjectPage slug={params.slug} />}
    </main>
  );
}
